import { Reader, Writer } from 'protobufjs/minimal';
import { codecContext } from './ultrabuf.context';
import { withProtobufWrappers } from './ultrabuf.wrappers';
import type { UltrabufTypeMap, CodecContext } from './ultrabuf.types';

const PROTOBUF_PACKED_TYPES = new Set<number>([
    1, // int32
    3, // bool
    4, // float
    5, // double
    6, // uint32
    7, // sint32
    8, // int64
    9, // uint64
    10, // sint64
    11, // fixed32
    12, // sfixed32
    13, // fixed64
    14, // sfixed64
]);

const DEFAULT_VALUE_TYPES = [
    16, // tencent.docs.core.slide.command.pb.Command
    22, // google.protobuf.BoolValue
    24, // google.protobuf.StringValue
    135, // google.protobuf.Int32Value
    359, // google.protobuf.DoubleValue
    8132, // google.protobuf.UInt32Value
    8237, // tencent.docs.api.slide.pb.Revision
];

type BasicTypeWriter = <T>(value: T) => Writer;

export type UltrabufDecodeProcessor = (
    message: any,
    fieldName: string,
    decodedValue: any,
    valueKey?: string | number,
) => void;
export type UltrabufEncodeProcessor = (message: any, fieldName: string, value: any) => void;

const WIRE_TYPE_LENGTH_DELIMITED = 2;

export class UltrabufCodec {
    public static context: CodecContext = withProtobufWrappers(codecContext);

    public decode<TypeIndex extends keyof UltrabufTypeMap>(
        buffer: Uint8Array,
        typeIndex: TypeIndex,
        processor?: UltrabufDecodeProcessor,
    ): UltrabufTypeMap[TypeIndex] {
        return decode(
            UltrabufCodec.context,
            Reader.create(buffer),
            typeIndex,
            null,
            processor,
        ) as UltrabufTypeMap[TypeIndex];
    }

    public encode<TypeIndex extends keyof UltrabufTypeMap>(
        message: UltrabufTypeMap[TypeIndex],
        typeIndex: TypeIndex,
        processor?: UltrabufEncodeProcessor,
    ): Uint8Array {
        const writer = Writer.create();
        const { int32 } = writer;
        // Fix for protobufjs Writer bug.
        // The raw writer.int32 func will encode value in range (-1, 0) into string instead of varint.
        writer.int32 = (value: number) => int32.call(writer, ~~value);
        return encode(UltrabufCodec.context, writer, message, typeIndex, processor).finish();
    }
}

// eslint-disable-next-line complexity
function decode(
    context: CodecContext,
    reader: Reader,
    typeIndex: number,
    length: number | null,
    processor?: UltrabufDecodeProcessor,
): unknown {
    const { typeTable, fieldTable, shiftMask, basicTypes, wrappers, mapTypes } = context;
    const { fieldShift, typeShift, repeatedShift, fieldMask, typeMask, repeatedMask } = shiftMask;

    if (typeIndex <= basicTypes.length) {
        return reader[basicTypes[typeIndex - 1]]?.();
    }

    const total = typeTable[typeIndex];
    const end = length === null ? reader.len : reader.pos + length;
    const message: Record<string, unknown> = {};

    if (DEFAULT_VALUE_TYPES.includes(typeIndex)) {
        for (let fieldIndex = typeIndex + 1; fieldIndex < typeIndex + total; fieldIndex++) {
            const fieldCode = typeTable[fieldIndex];
            const fieldTypeIndex = (fieldCode >>> typeShift) & typeMask;
            const repeated = (fieldCode >>> repeatedShift) & repeatedMask;
            if (fieldTypeIndex > basicTypes.length || repeated) {
                continue;
            }

            const fieldNameIndex = (fieldCode >>> fieldShift) & fieldMask;
            const fieldName = fieldTable[fieldNameIndex];
            message[fieldName] = getBasicTypeDefaultValue(fieldTypeIndex);
        }
    }

    while (reader.pos < end) {
        const tag = reader.uint32();
        const fieldOffset = tag >>> 3;
        const fieldCode = typeTable[typeIndex + fieldOffset];

        if (fieldOffset >= total || !fieldCode) {
            reader.skipType(tag & 7);
            continue;
        }

        const fieldNameIndex = (fieldCode >>> fieldShift) & fieldMask;
        const fieldTypeIndex = (fieldCode >>> typeShift) & typeMask;
        const repeated = (fieldCode >>> repeatedShift) & repeatedMask;
        const fieldName = fieldTable[fieldNameIndex];
        const fieldLength = fieldTypeIndex <= basicTypes.length ? 0 : reader.uint32();
        const isMapType = mapTypes.includes(fieldTypeIndex);

        const isPackedType = (tag & 7) === 2 && PROTOBUF_PACKED_TYPES.has(fieldTypeIndex);
        if (repeated && isPackedType) {
            const packedTypeEnd = reader.uint32() + reader.pos;
            while (reader.pos < packedTypeEnd) {
                append(
                    message,
                    fieldName,
                    decode(context, reader, fieldTypeIndex, fieldLength, processor),
                    repeated,
                    isMapType,
                    processor,
                );
            }
            continue;
        }

        append(
            message,
            fieldName,
            decode(context, reader, fieldTypeIndex, fieldLength, processor),
            repeated,
            isMapType,
            processor,
        );
    }

    const wrapper = wrappers.get(typeIndex);
    return wrapper ? wrapper.unwrap(message) : message;
}

// eslint-disable-next-line complexity
function encode(
    context: CodecContext,
    writer: Writer,
    message: any,
    typeIndex: number,
    processor?: UltrabufEncodeProcessor,
): Writer {
    const { typeTable, fieldTable, shiftMask, basicTypes, wireTypes, wrappers, mapTypes } = context;
    const { fieldShift, typeShift, fieldMask, typeMask, repeatedMask, repeatedShift } = shiftMask;

    const total = typeTable[typeIndex];

    for (let fieldOffset = 1; fieldOffset < total; fieldOffset++) {
        const fieldCode = typeTable[typeIndex + fieldOffset];

        if (!fieldCode) {
            continue;
        }

        const fieldNameIndex = (fieldCode >>> fieldShift) & fieldMask;
        const fieldName = fieldTable[fieldNameIndex];

        processor?.(message, fieldName, message[fieldName]);

        const fieldValue = message[fieldName];
        if (fieldValue === undefined || fieldValue === null) {
            continue;
        }

        const fieldTypeIndex = (fieldCode >>> typeShift) & typeMask;
        const fieldWireType = wireTypes.get(fieldTypeIndex) ?? WIRE_TYPE_LENGTH_DELIMITED;
        const tag = (fieldOffset << 3) | fieldWireType;
        const repeated = (fieldCode >>> repeatedShift) & repeatedMask;
        const wrapper = wrappers.get(fieldTypeIndex);
        const isMapType = mapTypes.includes(fieldTypeIndex);
        const values = repeated ? getRepeatedValues(fieldValue, isMapType) : [fieldValue];

        for (const value of values) {
            const wrappedValue = wrapper ? wrapper.wrap(value) : value;
            writer.uint32(tag);
            if (fieldTypeIndex <= basicTypes.length) {
                (writer[basicTypes[fieldTypeIndex - 1]] as BasicTypeWriter)(wrappedValue);
            } else {
                encode(context, writer.fork(), wrappedValue, fieldTypeIndex, processor).ldelim();
            }
        }
    }

    return writer;
}

function append(
    message: any,
    fieldName: any,
    decodedValue: any,
    repeated: number | boolean,
    isMapType: boolean,
    processor?: UltrabufDecodeProcessor,
) {
    if (!repeated) {
        message[fieldName] = decodedValue;
        processor?.(message, fieldName, decodedValue);
        return;
    }

    if (isMapType) {
        message[fieldName] ??= {};
        message[fieldName][decodedValue.key] = decodedValue.value;
        processor?.(message, fieldName, decodedValue.value, decodedValue.key);
        return;
    }

    message[fieldName] ??= [];
    message[fieldName].push(decodedValue);
    processor?.(message, fieldName, decodedValue, message[fieldName].length - 1);
}

function getRepeatedValues(fieldValue: any, isMapType: boolean) {
    if (isMapType) {
        return Object.entries(fieldValue).map((item) => ({
            key: item[0],
            value: item[1],
        }));
    }

    return fieldValue;
}

// eslint-disable-next-line complexity
function getBasicTypeDefaultValue(typeIndex: number): number | string | number | boolean | Uint8Array | undefined {
    switch (typeIndex) {
        /** int32 */
        case 1:
            return 0;
        /** string */
        case 2:
            return '';
        /** bool */
        case 3:
            return false;
        /** float */
        case 4:
            return 0;
        /** double */
        case 5:
            return 0;
        /** uint32 */
        case 6:
            return 0;
        /** sint32 */
        case 7:
            return 0;
        /** int64 */
        case 8:
            return 0;
        /** uint64 */
        case 9:
            return 0;
        /** sint64 */
        case 10:
            return 0;
        /** fixed32 */
        case 11:
            return 0;
        /** sfixed32 */
        case 12:
            return 0;
        /** fixed64 */
        case 13:
            return 0;
        /** sfixed64 */
        case 14:
            return 0;
        /** bytes */
        case 15:
            return new Uint8Array(0);
        default:
            return;
    }
}
